import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {DataService} from '../../../Services/data.service';
import {StorageService} from '../../../Services/storage.service';
import {BsToastService} from '../../../Services/bs-toast-service';
import {ConfirmDialogService} from '../../../Services/confirm-dialog.service';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-crm-customer-detail',
  templateUrl: './crm-customer-detail.component.html',
  styleUrls: ['./crm-customer-detail.component.css'],
  standalone: false
})
export class CrmCustomerDetailComponent implements OnInit {

  entityId = '';
  activeTab = 'overview';
  isLoading = true;

  // Overview
  profile: any = null;
  aging: any = null;

  // Interactions
  interactions: any[] = [];
  interactionsPage = 0;
  interactionsTotal = 0;
  showLogInteraction = false;
  newInteraction: any = {
    interaction_type: 'call',
    subject: '',
    body: '',
    outcome: 'neutral',
    follow_up_required: false
  };
  editingInteractionId: string | null = null;
  editInteractionForm: any = {};

  // Calendar events
  events: any[] = [];
  showScheduleEvent = false;
  newEvent: any = {
    title: '',
    description: '',
    start: '',
    allDay: false
  };

  // Opportunities
  opportunities: any[] = [];

  // Transactions
  transactions: any[] = [];
  txPage = 0;
  txPageSize = 20;
  txTotal = 0;
  txUnpaidOnly = false;

  // Contacts
  contacts: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private dataService: DataService,
    private localStorage: StorageService,
    private notification: BsToastService,
    private confirmDialog: ConfirmDialogService,
    private titleService: Title
  ) {
  }

  ngOnInit(): void {
    this.entityId = this.route.snapshot.paramMap.get('id') ?? '';
    this.titleService.setTitle('CRM — Customer Detail');
    this.loadOverview();
    this.loadInteractions();
    this.loadEvents();
    this.loadOpportunities();
    this.loadTransactions();
    this.loadContacts();
  }

  setTab(tab: string): void {
    this.activeTab = tab;
  }

  loadOverview(): void {
    this.isLoading = true;
    const profileUrl = 'crm/entities/' + this.entityId;
    this.dataService.getData(profileUrl).subscribe({
      next: (result: any) => {
        this.profile = result;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
    const agingUrl = 'crm/entities/' + this.entityId + '/aging';
    this.dataService.getData(agingUrl).subscribe({
      next: (result: any) => {
        this.aging = result;
      },
      error: () => {
      }
    });
  }

  startEditInteraction(i: any): void {
    this.editingInteractionId = i.interactionId ?? i.id;
    this.editInteractionForm = {
      interaction_type: i.interactionType ?? i.interaction_type,
      subject: i.subject,
      body: i.body,
      outcome: i.outcome,
      follow_up_required: i.followUpRequired ?? i.follow_up_required
    };
  }

  deleteInteraction(i: any): void {
    const id = i.interactionId ?? i.id;
    this.confirmDialog.confirmThis(
      ['Delete this interaction?', 'This cannot be undone.'],
      () => {
        this.dataService.deleteDataById('crm/interactions/' + id, '').subscribe({
          next: () => {
            this.notification.showSuccessToast('Interaction deleted.');
            this.loadInteractions();
          },
          error: () => {
            this.notification.showErrorToast('Failed to delete interaction.');
          }
        });
      },
      () => {
      }
    );
  }


  cancelEditInteraction(): void {
    this.editingInteractionId = null;
    this.editInteractionForm = {};
  }

  submitEditInteraction(): void {
    if (!this.editingInteractionId) {
      return;
    }
    this.dataService.putDataByArray('crm/interactions/' + this.editingInteractionId, this.editInteractionForm).subscribe({
      next: () => {
        this.notification.showSuccessToast('Interaction updated.');
        this.editingInteractionId = null;
        this.editInteractionForm = {};
        this.loadInteractions();
      },
      error: () => {
        this.notification.showErrorToast('Failed to update interaction.');
      }
    });
  }

  loadInteractions(): void {
    this.dataService.getData(
      'crm/entities/' + this.entityId + '/interactions?page=' + this.interactionsPage + '&pageSize=20'
    ).subscribe({
      next: (result: any) => {
        this.interactions = result?.items ?? result ?? [];
        this.interactionsTotal = result?.total ?? this.interactions.length;
      },
      error: () => {
      }
    });
  }

  submitInteraction(): void {
    this.dataService.post('crm/entities/' + this.entityId + '/interactions', this.newInteraction).subscribe({
      next: () => {
        this.notification.showSuccessToast('Interaction logged successfully.');
        this.showLogInteraction = false;
        this.newInteraction = {interaction_type: 'call', subject: '', body: '', outcome: 'neutral', follow_up_required: false};
        this.loadInteractions();
      },
      error: () => {
        this.notification.showErrorToast('Failed to log interaction.');
      }
    });
  }

  loadEvents(): void {
    this.dataService.getData('crm/entities/' + this.entityId + '/events').subscribe({
      next: (result: any) => {
        this.events = result ?? [];
      },
      error: () => {
      }
    });
  }

  deleteEvent(ev: any): void {
    const id = ev.eventId ?? ev.id;
    this.confirmDialog.confirmThis(
      ['Delete this calendar event?', 'This cannot be undone.'],
      () => {
        this.dataService.deleteDataById('crm/entities/' + this.entityId + '/events/' + id, '').subscribe({
          next: () => {
            this.notification.showSuccessToast('Event deleted.');
            this.loadEvents();
          },
          error: () => {
            this.notification.showErrorToast('Failed to delete event.');
          }
        });
      },
      () => {
      }
    );
  }

  submitEvent(): void {
    this.dataService.post('crm/entities/' + this.entityId + '/events', this.newEvent).subscribe({
      next: () => {
        this.notification.showSuccessToast('Follow-up scheduled.');
        this.showScheduleEvent = false;
        this.newEvent = {title: '', description: '', start: '', allDay: false};
        this.loadEvents();
      },
      error: () => {
        this.notification.showErrorToast('Failed to schedule event.');
      }
    });
  }

  loadOpportunities(): void {
    this.dataService.getData('crm/entities/' + this.entityId + '/opportunities').subscribe({
      next: (result: any) => {
        this.opportunities = result ?? [];
      },
      error: () => {
      }
    });
  }

  get txTotalPages(): number {
    return Math.ceil(this.txTotal / this.txPageSize) || 1;
  }

  get txPageEnd(): number {
    return Math.min((this.txPage + 1) * this.txPageSize, this.txTotal);
  }

  txPrevPage(): void {
    if (this.txPage > 0) {
      this.txPage--;
      this.loadTransactions();
    }
  }

  txNextPage(): void {
    if ((this.txPage + 1) < this.txTotalPages) {
      this.txPage++;
      this.loadTransactions();
    }
  }

  loadTransactions(): void {
    let params = '?page=' + this.txPage + '&pageSize=' + this.txPageSize;
    if (this.txUnpaidOnly) {
      params += '&unpaidOnly=true';
    }
    const txUrl = 'crm/entities/' + this.entityId + '/transactions' + params;
    this.dataService.getData(txUrl).subscribe({
      next: (result: any) => {
        this.transactions = result?.items ?? result ?? [];
        this.txTotal = result?.total ?? this.transactions.length;
      },
      error: () => {
      }
    });
  }

  loadContacts(): void {
    const contactsUrl = 'crm/entities/' + this.entityId + '/contacts';
    this.dataService.getData(contactsUrl).subscribe({
      next: (result: any) => {
        console.log('crm/entities contacts result:', result);
        this.contacts = result ?? [];
      },
      error: () => {
      }
    });
  }

  onTxUnpaidChange(): void {
    this.txPage = 0;
    this.loadTransactions();
  }

  interactionTypeLabel(type: string): string {
    const map: any = {call: 'Call', email: 'Email', meeting: 'Meeting', note: 'Note', demo: 'Demo', site_visit: 'Site Visit'};
    return map[type] ?? type;
  }

  interactionTypeIcon(type: string): string {
    const map: any = {
      call: 'bi-telephone', email: 'bi-envelope', meeting: 'bi-calendar-event',
      note: 'bi-sticky', demo: 'bi-display', site_visit: 'bi-geo-alt'
    };
    return map[type] ?? 'bi-chat';
  }

  outcomeClass(outcome: string): string {
    if (outcome === 'positive') {
      return 'text-success';
    }
    if (outcome === 'negative') {
      return 'text-danger';
    }
    return 'text-muted';
  }
}
